import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, DialogActions, Modal, TextField, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import FirebaseEmailAuthService from "src/auth/services/FirebaseAuthenticationService/emailAuth/FirebaseEmailAuthentication.service";
import { useSnackbar } from "src/theme/SnackbarProvider/SnackbarProvider";
import { FirebaseError, getUserFriendlyFirebaseErrorMessage } from "src/error/FirebaseError/firebaseError";
import CustomLink from "src/theme/CustomLink/CustomLink";
import { IsOnlineContext } from "src/app/isOnlineProvider/IsOnlineProvider";
import PrimaryIconButton from "src/theme/PrimaryIconButton/PrimaryIconButton";
import PrimaryButton from "src/theme/PrimaryButton/PrimaryButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { enqueueErrorSnackbarWithReference } from "src/theme/SnackbarProvider/enqueueErrorSnackbarWithReference";

const COOLDOWN_SECONDS = 60;

export const DATA_TEST_ID = {
  CONTAINER: "reset-password-email-container",
  RESET_LINK: "reset-password-email-link",
  TIMER: "reset-password-email-timer",
  DIALOG: "reset-password-email-dialog",
  DIALOG_TITLE: "reset-password-email-dialog-title",
  CLOSE_ICON: "reset-password-email-close-icon",
  INPUT: "reset-password-email-input",
  SUBMIT: "reset-password-email-submit",
  CANCEL: "reset-password-email-cancel",
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: 400,
  width: "100%",
  backgroundColor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  padding: (theme: Theme) => theme.fixedSpacing(theme.tabiyaSpacing.md),
};

interface ResetPasswordEmailSenderProps {
  initialCooldownSeconds?: number;
}

const ResetPasswordEmailSender: React.FC<ResetPasswordEmailSenderProps> = ({ initialCooldownSeconds = 0 }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(initialCooldownSeconds);
  const { enqueueSnackbar } = useSnackbar();
  const isOnline = useContext(IsOnlineContext);

  const isSmallMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldownSeconds > 0) {
      timer = setInterval(() => {
        setCooldownSeconds((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const handleSendResetEmail = useCallback(async () => {
    setIsLoading(true);
    if (!emailInput) {
      enqueueSnackbar(t("auth.components.passwordReset.pleaseEnterEmail"), { variant: "warning" });
      console.warn("Password reset attempt failed: email input missing.");
      setIsLoading(false);
      return;
    }
    try {
      const authService = FirebaseEmailAuthService.getInstance();
      await authService.resetPassword(emailInput);
      enqueueSnackbar(t("auth.components.passwordReset.passwordResetEmailSent"), { variant: "success" });
      setCooldownSeconds(COOLDOWN_SECONDS);
    } catch (error) {
      console.error("Password reset request failed:", error);
      if (error instanceof FirebaseError) {
        enqueueSnackbar(getUserFriendlyFirebaseErrorMessage(error), { variant: "error" });
      } else {
        enqueueErrorSnackbarWithReference(t("auth.components.passwordReset.failedToSendResetEmail"), {
          where: "Password reset",
          error,
        });
      }
    } finally {
      setIsLoading(false);
      setDialogOpen(false);
    }
  }, [emailInput, enqueueSnackbar, t]);

  return (
    <Box
      data-testid={DATA_TEST_ID.CONTAINER}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: theme.spacing(1),
      }}
    >
      {cooldownSeconds !== initialCooldownSeconds && (
        <Typography variant="body2" color="info">
          {t("auth.components.passwordReset.passwordResetLinkSent")}
        </Typography>
      )}
      <Box>
        <CustomLink
          onClick={() => setDialogOpen(true)}
          disabled={isLoading || cooldownSeconds > 0 || !isOnline}
          data-testid={DATA_TEST_ID.RESET_LINK}
          sx={{
            textDecoration: "Underline",
            fontWeight: 600,
            whiteSpace: "normal",
            "&:hover": {
              color: theme.palette.common.white,
              opacity: 0.75,
            },
          }}
        >
          {t("auth.components.passwordReset.forgotPassword")}
        </CustomLink>
        {cooldownSeconds > 0 && (
          <Typography
            variant="caption"
            color="textSecondary"
            component="span"
            sx={{ ml: 1 }}
            data-testid={DATA_TEST_ID.TIMER}
          >
            ({cooldownSeconds}s)
          </Typography>
        )}
      </Box>

      <Modal open={dialogOpen} onClose={() => setDialogOpen(false)} data-testid={DATA_TEST_ID.DIALOG}>
        <Box
          sx={{
            ...style,
            padding: isSmallMobile ? theme.fixedSpacing(theme.tabiyaSpacing.md) : theme.tabiyaSpacing.lg,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="start"
            sx={{ mb: theme.fixedSpacing(theme.tabiyaSpacing.sm) }}
          >
            <Typography variant="h4" gutterBottom data-testid={DATA_TEST_ID.DIALOG_TITLE}>
              {t("auth.components.passwordReset.resetPassword")}
            </Typography>
            <PrimaryIconButton
              title={t("auth.components.passwordReset.closePasswordResetForm")}
              onClick={() => setDialogOpen(false)}
              sx={{
                color: theme.palette.grey[500],
              }}
              data-testid={DATA_TEST_ID.CLOSE_ICON}
            >
              <CloseIcon />
            </PrimaryIconButton>
          </Box>
          <TextField
            placeholder={t("common.fields.email")}
            type="email"
            fullWidth
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            autoFocus
            data-testid={DATA_TEST_ID.INPUT}
          />
          <DialogActions sx={{ paddingLeft: 0, paddingRight: 0 }}>
            <PrimaryButton
              type="submit"
              disableWhenOffline={true}
              showCircle
              disabled={!emailInput || isLoading}
              data-testid={DATA_TEST_ID.SUBMIT}
              onClick={handleSendResetEmail}
            >
              {t("common.buttons.submit")}
            </PrimaryButton>
          </DialogActions>
        </Box>
      </Modal>
    </Box>
  );
};

export default ResetPasswordEmailSender;
